/**
 * Labour Payment Runs — Generate, review and approve weekly/fortnightly pay runs.
 * Route: /hr/labour/payment-runs
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';
import {
  Plus, RefreshCw, CheckCircle2, XCircle, ChevronRight,
  Loader2, Download, Users, Clock, Banknote,
} from 'lucide-react';
import { formatINR, formatDate } from '../../lib/format';
import PageHeader from '../../components/PageHeader';

const STATUS_BADGE = {
  draft:    'bg-slate-100 text-slate-600 border-slate-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  paid:     'bg-green-100 text-green-800 border-green-200',
  voided:   'bg-red-50 text-red-500 border-red-200',
};

export default function LabourPaymentRuns() {
  const nav = useNavigate();
  const [runs, setRuns] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [actBusy, setActBusy] = useState(false);

  const [form, setForm] = useState({
    project_id: '',
    date_from: new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10),
    date_to: new Date().toISOString().slice(0, 10),
    notes: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, pr] = await Promise.all([
        apiGet('/labour/payment-runs'),
        apiGet('/projects'),
      ]);
      setRuns(r.items || []);
      setProjects(Array.isArray(pr) ? pr : (pr?.projects || []));
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async (e) => {
    e.preventDefault();
    if (!form.date_from || !form.date_to) { toast.error('Date range required'); return; }
    setGenerating(true);
    try {
      const run = await apiPost('/labour/payment-runs', { ...form, project_id: form.project_id || undefined });
      toast.success(`Payment run created — ₹${formatINR(run.total_pay)} for ${run.labour_count} workers`);
      setShowNew(false);
      load();
      setSelected(run);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Generation failed'); }
    setGenerating(false);
  };

  const approve = async (run) => {
    setActBusy(true);
    try {
      await apiPost(`/labour/payment-runs/${run.id}/approve`);
      toast.success('Payment run approved');
      const updated = await apiGet(`/labour/payment-runs/${run.id}`);
      setSelected(updated);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
    setActBusy(false);
  };

  const voidRun = async (run) => {
    if (!window.confirm('Void this payment run? This cannot be undone.')) return;
    setActBusy(true);
    try {
      await apiPost(`/labour/payment-runs/${run.id}/void`);
      toast.success('Payment run voided');
      const updated = await apiGet(`/labour/payment-runs/${run.id}`);
      setSelected(updated);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Void failed'); }
    setActBusy(false);
  };

  // ── Detail panel ──────────────────────────────────────────────────────
  if (selected) return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <button onClick={() => setSelected(null)} className="text-xs text-slate-500 hover:text-slate-800">← All Runs</button>
        <span className="text-slate-300">|</span>
        <span className="text-xs font-semibold text-slate-700">
          Run: {selected.date_from} → {selected.date_to}
        </span>
        <span className={`ml-2 text-[10px] px-2 py-0.5 border rounded-sm font-bold uppercase ${STATUS_BADGE[selected.status] || ''}`}>
          {selected.status}
        </span>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KPI icon={Users} label="Workers" value={selected.labour_count} />
        <KPI icon={Clock} label="Total Shifts" value={selected.total_shifts?.toFixed(2)} />
        <KPI icon={Clock} label="Total Hours" value={selected.total_hours?.toFixed(1)} />
        <KPI icon={Banknote} label="Total Pay" value={formatINR(selected.total_pay)} highlight />
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        {selected.status === 'draft' && (
          <button onClick={() => approve(selected)} disabled={actBusy}
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-sm text-xs font-bold disabled:opacity-50"
            data-testid="approve-run-btn">
            {actBusy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
            Approve
          </button>
        )}
        {['draft', 'approved'].includes(selected.status) && (
          <button onClick={() => voidRun(selected)} disabled={actBusy}
            className="flex items-center gap-1.5 px-4 py-2 border border-red-300 text-red-600 rounded-sm text-xs font-bold disabled:opacity-50"
            data-testid="void-run-btn">
            <XCircle size={13} /> Void
          </button>
        )}
      </div>

      {/* Line items table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="run-line-items">
        <table className="w-full dense-table text-xs">
          <thead><tr>
            <th>Worker</th><th className="text-right">Normal hrs</th>
            <th className="text-right">OT hrs</th><th className="text-right">Total Shifts</th>
            <th className="text-right">Normal Pay</th><th className="text-right">OT Pay</th>
            <th className="text-right font-bold">Total Pay</th>
          </tr></thead>
          <tbody>
            {(selected.line_items || []).map((li, i) => (
              <tr key={i} data-testid={`run-li-${i}`}>
                <td className="font-semibold">{li.labour_name || li.labour_id}</td>
                <td className="text-right tabular-nums">{li.normal_hours?.toFixed(2)}</td>
                <td className="text-right tabular-nums">{li.overtime_hours?.toFixed(2)}</td>
                <td className="text-right tabular-nums font-semibold">{li.total_shifts?.toFixed(2)}</td>
                <td className="text-right tabular-nums">{formatINR(li.normal_pay)}</td>
                <td className="text-right tabular-nums">{formatINR(li.overtime_pay)}</td>
                <td className="text-right tabular-nums font-display font-bold text-[#3b4872]">{formatINR(li.total_pay)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-50">
            <tr>
              <td className="font-bold">TOTAL</td>
              <td className="text-right tabular-nums font-bold">{selected.total_hours?.toFixed(1)}</td>
              <td /><td className="text-right tabular-nums font-bold">{selected.total_shifts?.toFixed(2)}</td>
              <td /><td />
              <td className="text-right tabular-nums font-display font-extrabold text-[#3b4872]">{formatINR(selected.total_pay)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );

  // ── List view ─────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      <PageHeader overline="People & HR · Labour" title="Payment Runs"
        subtitle="Generate weekly/fortnightly labour pay runs. Each run computes shifts + OT for every worker in the date window."
        actions={(
          <button onClick={() => setShowNew(!showNew)}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#3b4872] text-white rounded-sm text-xs font-bold hover:bg-[#2d3856]"
            data-testid="new-run-btn">
            <Plus size={13} /> New Pay Run
          </button>
        )}
      />

      {showNew && (
        <form onSubmit={generate}
          className="bg-white border border-slate-200 rounded-sm p-5 space-y-4"
          data-testid="new-run-form">
          <div className="font-bold text-sm text-slate-800">Generate Payment Run</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Project (optional — leave blank for all)</label>
              <select value={form.project_id} onChange={e => setForm({ ...form, project_id: e.target.value })}
                className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#3b4872]"
                data-testid="run-project-select">
                <option value="">All projects</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Notes (optional)</label>
              <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                placeholder="e.g. Week of 9–15 Jun 2026"
                className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">From Date *</label>
              <input type="date" value={form.date_from} onChange={e => setForm({ ...form, date_from: e.target.value })}
                className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]"
                data-testid="run-date-from" required />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">To Date *</label>
              <input type="date" value={form.date_to} onChange={e => setForm({ ...form, date_to: e.target.value })}
                className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]"
                data-testid="run-date-to" required />
            </div>
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={generating}
              className="flex items-center gap-1.5 px-5 py-2 bg-[#3b4872] text-white rounded-sm text-xs font-bold disabled:opacity-50"
              data-testid="generate-run-btn">
              {generating ? <Loader2 size={13} className="animate-spin" /> : null}
              {generating ? 'Generating…' : 'Generate Run'}
            </button>
            <button type="button" onClick={() => setShowNew(false)} className="px-4 py-2 border border-slate-300 rounded-sm text-xs text-slate-600 font-bold">Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 py-8"><Loader2 className="animate-spin" size={16} />Loading…</div>
      ) : runs.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-slate-400">
          <Banknote size={28} className="mx-auto mb-3 opacity-40" />
          <div className="font-semibold">No payment runs yet</div>
          <div className="text-xs mt-1">Click "New Pay Run" to generate the first run.</div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm divide-y divide-slate-100" data-testid="runs-list">
          {runs.map(r => (
            <button key={r.id} onClick={() => { setLoading(true); apiGet(`/labour/payment-runs/${r.id}`).then(setSelected).catch(() => {}).finally(() => setLoading(false)); }}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 text-left"
              data-testid={`run-row-${r.id}`}>
              <div>
                <div className="font-semibold text-sm">{r.date_from} → {r.date_to}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {r.labour_count} worker(s) · {r.total_shifts?.toFixed(2)} shifts · {formatINR(r.total_pay)}
                  {r.notes && <span className="ml-2 text-slate-400">· {r.notes}</span>}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className={`text-[10px] px-2 py-0.5 border rounded-sm font-bold uppercase ${STATUS_BADGE[r.status] || ''}`}>{r.status}</span>
                <ChevronRight size={15} className="text-slate-400" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, highlight = false }) {
  return (
    <div className={`bg-white border rounded-sm p-4 ${highlight ? 'border-[#3b4872]/30' : 'border-slate-200'}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
        <Icon size={12} /> {label}
      </div>
      <div className={`font-display font-extrabold text-xl ${highlight ? 'text-[#3b4872]' : 'text-slate-900'}`}>{value ?? '—'}</div>
    </div>
  );
}
